import { useAuth } from '@/store/auth/auth.store';
import {
  contractErrorMessage,
  contractNotStarted,
  formatContractDay,
  invitationContractDays,
  mapContract,
} from '@/store/contracts/contracts.mappers';
import {
  useAcceptInviteMutation,
  useDeclineInviteMutation,
  useSingleInviteQuery,
} from '@/store/invites/invites.store';
import { ownershipErrorMessage } from '@/store/ownership/ownership.mappers';
import { useVessels } from '@/store/vessels/vessels.store';
import { toast } from '@/utils/toast';

/**
 * Accepting or declining an invitation from a notification. A temporary one accepted before its
 * contract starts only books the start: the invitee joins the vessel then, so nothing switches to
 * it now.
 */
export const useInviteResponse = (id: string, serviceId: string) => {
  const { data: invitation } = useSingleInviteQuery(id);
  const { getMyVessels, selectVessel } = useVessels();
  const { initialize } = useAuth();

  const { mutateAsync: acceptInvite, isPending: isAccepting } = useAcceptInviteMutation();
  const { mutate: declineInvite, isPending: isDeclining } = useDeclineInviteMutation();

  const contract = mapContract(invitation?.contract);
  const days = contract ? invitationContractDays(contract, invitation?.contract_days) : undefined;
  const firstDay = days && formatContractDay(days.firstDay);

  const accept = async () => {
    if (!invitation) return;

    try {
      await acceptInvite(invitation.id);
    } catch (err) {
      console.error(err);
      toast.error(
        contractErrorMessage(
          err,
          ownershipErrorMessage(err, 'Couldn’t accept the invitation. Please try again.'),
        ),
      );
      return;
    }

    if (contract && contractNotStarted(contract)) {
      toast.success(`Accepted. You'll join automatically on ${firstDay}.`);
      return;
    }

    await initialize();
    await getMyVessels([serviceId]);
    selectVessel(serviceId);
  };

  const decline = (reason?: string) => {
    if (invitation) declineInvite({ id: invitation.id, reason });
  };

  return {
    invitation,
    accepted: !!invitation?.accepted_at,
    accept,
    decline,
    pending: isAccepting || isDeclining,
  };
};
